import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { inject, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ListView } from '@app/shared/utils/list-view';
import { firstValueFrom } from 'rxjs';
import { ModulesService } from '@shared/services/modules.service';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

import { MaterialModule } from '@app/material.module';
import { CustomTableModel } from '@app/shared/models/custom-table.model';
import { TableStructureModel } from '@app/shared/models/table.model';
import { CustomTableComponent } from '@app/custom-material/custom-table/custom-table.component';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { UploadFileDialogService } from '@app/upload-file-dialog/upload-file-dialog.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';

import { RolePermissionsService } from '@shared/services/auth/role-permissions.service';
import { UsersService } from '@app/shared/services/entities/users.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { StockSapService } from '@app/shared/services/entities/stockSap.service';
import { StockLoadsService } from '@app/shared/services/entities/stockLoads.service';

import { CedisDiffDialogComponent, CedisDiffData, CedisDiffRow } from './cedis-diff-dialog.component';

export type SapKind = 'CONSIGNA' | 'PLANTA';

/** Columnas de cada reporte de SAP, en el orden del archivo. */
export const SAP_KIND_COLUMNS: Record<SapKind, string[]> = {
  CONSIGNA: [
    'NombreCliente', 'SoldTo', 'Linea', 'CFN', 'Codigo Institucion', 'Descripcion de producto',
    'UPN', 'Lote', 'Cantidad Total', 'Fecha de Vencimiento', 'Ciudad', 'Pais', 'Div', 'MPG',
    'DayToExp', 'Serial', 'Planta', 'VAS o NO VAS',
  ],
  // CFN, UPN y Lote vienen en el archivo de planta y son los que identifican el insumo.
  PLANTA: [
    'Nombre_Planta', 'CFN', 'Descripcion_Producto', 'UPN', 'Lote', 'QtyDisponible', 'QtyBloqued', 'QtyQI',
    'QtyRestricted', 'Fecha_Vencimiento', 'UOM', 'Sloc', 'ProfCenter',
  ],
};

const SAP_KIND_META: Record<SapKind, { route: string; title: string; qty: string; desc: string; date: string; file: string }> = {
  CONSIGNA: { route: '/inventoryCedis', title: 'Inventario SAP Consigna', qty: 'Cantidad Total',
              desc: 'Descripcion de producto', date: 'Fecha de Vencimiento', file: 'SAP-Consigna' },
  PLANTA: { route: '/inventorySapPlanta', title: 'Inventario SAP Planta', qty: 'QtyDisponible',
            desc: 'Descripcion_Producto', date: 'Fecha_Vencimiento', file: 'SAP-Planta' },
};

/**
 * Inventario SAP Consigna e Inventario SAP Planta (misma pantalla, distinto
 * reporte; la ruta dice cuál con `data.sapKind`).
 *
 * SAP manda y esta pantalla es un espejo de solo lectura. Cada corte es UN
 * archivo; se elige por fecha del corte y nombre (por omisión el más
 * reciente). Al cargar uno nuevo se previsualiza el cambio contra el que está
 * en pantalla. Consigna y Planta llegan en archivos distintos y cada uno
 * tiene su propio historial. Surtir (Solicitud de material) toma los FSL del
 * último corte de Planta.
 */
@Component({
  standalone: true,
  selector: 'app-inventory-cedis',
  imports: [PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, FormsModule, MaterialModule, CustomTableComponent],
  templateUrl: './inventory-cedis.component.html',
  styleUrls: ['./inventory-cedis.component.scss'],
})
export class InventoryCedisComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** CONSIGNA (ruta /inventoryCedis) o PLANTA (ruta /inventorySapPlanta). */
  readonly kind: SapKind = (inject(ActivatedRoute).snapshot.data?.['sapKind'] === 'PLANTA') ? 'PLANTA' : 'CONSIGNA';
  readonly meta = SAP_KIND_META[this.kind];
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle(this.meta.route, this.meta.title);
  readonly listView = new ListView(this.kind === 'PLANTA' ? 'sapPlanta' : 'sapConsigna', 'table');
  /** Campos de las tarjetas: título, subtítulo y detalle. */
  readonly cardFields = this.kind === 'PLANTA'
    ? { title: 'CFN', sub: 'Descripcion_Producto', fields: ['Nombre_Planta', 'Lote', 'QtyDisponible', 'QtyBloqued', 'QtyQI', 'QtyRestricted', 'Fecha_Vencimiento', 'Sloc'] }
    : { title: 'CFN', sub: 'Descripcion de producto', fields: ['NombreCliente', 'SoldTo', 'Lote', 'Cantidad Total', 'Fecha de Vencimiento', 'Ciudad', 'Planta', 'Serial'] };


  public permissions: { [key: string]: any } = {};
  public customParams: { [key: string]: any } = {};

  /** Corte elegido en el filtro (id del documento de carga). */
  public selectedLoadId: string | null = null;
  private productsByCode: { [code: string]: any } = {};

  public rows: any[] = [];
  public lastLoad: any = null;
  public loadsList: any[] = [];
  public showLoads = false;

  public kpis: { label: string; value: string | number }[] = [];
  public tableData: CustomTableModel = { columns: [], data: [] };
  public dataSource: any = new MatTableDataSource([]);
  public filterText = '';

  constructor(
    public dialog: MatDialog,
    private customMessageService: CustomMessageService,
    private uploadFileDialog: UploadFileDialogService,
    private simpleExcel: SimpleExcelService,
    private permissionsService: RolePermissionsService,
    private usersService: UsersService,
    private productsService: ProductsService,
    private stockSapService: StockSapService,
    private stockLoadsService: StockLoadsService,
  ) {}

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['stockSap', 'stockLoads'], () => this.pageLoad.ready && this.pageLoad.refresh(this.refresh()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    // Si falla, la pantalla de carga muestra el error con "Reintentar".
    await this.getRolNameByUserId();
    await Promise.all([this.getLoads(), this.getProducts()]);
    this.selectedLoadId = this.loadsList[0]?.id ?? null;   // el más reciente
    await this.getSnapshot();
  }

  // ─── Cargas iniciales ────────────────────────────────────────────────────

  private async getRolNameByUserId(): Promise<void> {
    const userId = sessionStorage.getItem('userId');
    const apiResponse: any = await this.usersService.getRolNameByUserId({ id: userId });
    this.customParams = apiResponse?.data?.[0] ?? {};
    // Permisos del módulo CEDIS; si no existe ese módulo, comparte los del
    // inventario de productos (quien carga inventario ahí, carga cortes aquí).
    this.permissions = await this.permissionsService.forRoute(this.meta.route, '/inventoryCedis', '/inventory');
  }

  private async getProducts(): Promise<void> {
    const apiResponse = await this.productsService.search({ searchType: 'default', condition: { deleted: false } });
    if (apiResponse.state !== 'success') return;
    this.productsByCode = (apiResponse.data as any[]).reduce((r, a) => {
      if (a.code) r[String(a.code).trim().toUpperCase()] = a;
      return r;
    }, Object.create(null));
  }

  // ─── Consulta del corte ──────────────────────────────────────────────────

  /** Etiqueta de un corte en el filtro: fecha del corte · nombre del archivo. */
  public loadLabel(load: any): string {
    if (!load) return '';
    return `${load.snapshotDate || this.formatDate(load.createdOn)} · ${load.fileName || this.meta.title}`;
  }

  public get selectedLoad(): any {
    return this.loadsList.find(l => l.id === this.selectedLoadId) ?? null;
  }

  /** Cortes cargados, del más reciente al más antiguo (fecha del corte y luego fecha de carga). */
  private async getLoads(): Promise<void> {
    const response: any = await this.stockLoadsService.search({ searchType: 'default', condition: { deleted: false } });
    this.loadsList = ((response?.data as any[]) ?? [])
      // Cada módulo ve solo sus cortes; los anteriores a la separación (sin kind) son de Consigna.
      .filter(l => l && l.status !== 'CANCELADO' && (l.kind ?? 'CONSIGNA') === this.kind)
      .sort((a, b) => String(b.snapshotDate ?? '').localeCompare(String(a.snapshotDate ?? ''))
        || InventoryCedisComponent.toTime(b.createdOn) - InventoryCedisComponent.toTime(a.createdOn));
  }

  /** Cambio de archivo en el filtro. */
  async onLoadChange(): Promise<void> {
    this.showLoads = false;
    await this.getSnapshot();
  }

  async selectLoad(id: string): Promise<void> {
    this.selectedLoadId = id;
    await this.onLoadChange();
  }

  /** Columnas del reporte (se guardan en `products` con este nombre). */
  get columns(): string[] { return SAP_KIND_COLUMNS[this.kind]; }
  private static readonly DATE_COLUMNS = new Set(['Fecha de Vencimiento', 'Fecha_Vencimiento']);

  /** Registro vigente (el de la carga más reciente) de la ubicación. */
  public currentSnapshot: any = null;

  private static toTime(v: any): number {
    const t = Date.parse(String(v ?? ''));
    return isNaN(t) ? 0 : t;
  }

  async getSnapshot(): Promise<void> {
    this.lastLoad = this.selectedLoad;
    if (!this.selectedLoadId) {
      this.currentSnapshot = null;
      this.rows = [];
      this.buildKpis();
      this.buildTable();
      return;
    }
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Cargando corte...', loading: true });
    try {
      const response: any = await this.stockSapService.search({
        searchType: 'default', condition: { deleted: false, loadId: this.selectedLoadId },
      });
      const snapshots = ((response?.data as any[]) ?? []).filter(x => x && Array.isArray(x.products))
        .sort((a, b) => InventoryCedisComponent.toTime(b.createdOn) - InventoryCedisComponent.toTime(a.createdOn));
      this.currentSnapshot = snapshots[0] ?? null;
      this.rows = this.currentSnapshot?.products ?? [];
      this.buildKpis();
      this.buildTable();
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true, dismissBtnText: 'Cerrar' });
    } finally {
      this.customMessageService.close();
    }
  }

  /** Recarga la lista de cortes y el elegido (botón Actualizar). */
  async refresh(): Promise<void> {
    await this.getLoads();
    if (!this.loadsList.some(l => l.id === this.selectedLoadId)) this.selectedLoadId = this.loadsList[0]?.id ?? null;
    await this.getSnapshot();
  }

  private static num(v: any): number {
    const n = Number(String(v ?? '').replace(/,/g, ''));
    return isNaN(n) ? 0 : n;
  }

  /** Cantidad de un renglón: Cantidad Total (Consigna) o QtyDisponible (Planta). */
  private qtyOf(r: any): number { return InventoryCedisComponent.num(r?.[this.meta.qty]); }

  private buildKpis(): void {
    const sum = (col: string) => this.rows.reduce((s, r) => s + InventoryCedisComponent.num(r?.[col]), 0);
    const distinct = (col: string) => new Set(this.rows.map(r => r?.[col]).filter(Boolean)).size;
    const date = this.currentSnapshot ? (this.currentSnapshot.snapshotDate || this.formatDate(this.currentSnapshot.createdOn)) : 'Sin cargas';
    this.kpis = this.kind === 'PLANTA' ? [
      { label: 'Renglones', value: this.rows.length },
      { label: 'Disponibles', value: sum('QtyDisponible') },
      { label: 'Bloqueadas / QI / Restringidas', value: sum('QtyBloqued') + sum('QtyQI') + sum('QtyRestricted') },
      { label: 'Plantas', value: distinct('Nombre_Planta') },
      { label: 'Fecha del corte', value: date },
    ] : [
      { label: 'Renglones', value: this.rows.length },
      { label: 'Piezas', value: sum('Cantidad Total') },
      { label: 'CFN distintos', value: distinct('CFN') },
      { label: 'Cuentas', value: distinct('NombreCliente') },
      { label: 'Fecha del corte', value: date },
    ];
  }

  /** Columnas del reporte, siempre en el orden del archivo de SAP. */
  private get presentColumns(): string[] { return this.columns; }

  /** Renglones filtrados para la vista de tarjetas. */
  get cardRows(): any[] { return this.filteredRows().slice(0, this.cardLimit); }
  cardLimit = 60;

  private buildTable(): void {
    const cols = this.presentColumns;
    const columns = cols.map((c, i) => ({ columnKey: 'c' + i, columnName: c }));
    const data = this.filteredRows().map(r => {
      const o: any = {};
      cols.forEach((c, i) => o['c' + i] = r?.[c] ?? '—');
      return o;
    });
    this.tableData = { columns, data };
    this.dataSource = new MatTableDataSource(data);
  }

  private filteredRows(): any[] {
    const f = this.filterText.trim().toLowerCase();
    if (!f) return this.rows;
    return this.rows.filter(r => Object.values(r ?? {}).some(v => String(v ?? '').toLowerCase().includes(f)));
  }

  applyFilter(event: Event | string): void {
    this.filterText = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;
    this.cardLimit = 60;
    this.buildTable();
  }

  toggleLoads(): void {
    this.showLoads = !this.showLoads;
  }

  // ─── Carga del corte (Excel de SAP) ──────────────────────────────────────

  /** Encabezado normalizado: sin acentos, espacios, guiones bajos ni mayúsculas. */
  private static headerKey(key: string): string {
    return String(key ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_.\-]+/g, '').toUpperCase();
  }

  private static toIsoDate(value: any): any {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) {
      return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(value.getUTCDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'number') {
      const date = new Date((value - 25569) * 86400 * 1000);
      return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    }
    return String(value).trim();
  }

  /** Cada renglón del Excel → objeto con las columnas de SAP (se guarda tal cual en `products`). */
  private mapExcelRows(items: any[]): { records: any[]; skipped: number } {
    const byKey = new Map(this.columns.map(c => [InventoryCedisComponent.headerKey(c), c]));
    const records: any[] = [];
    let skipped = 0;
    for (const row of items) {
      const rec: any = {};
      let hasValue = false;
      for (const k of Object.keys(row ?? {})) {
        const col = byKey.get(InventoryCedisComponent.headerKey(k));
        if (!col || rec[col] !== undefined) continue;
        let v = row[k];
        if (InventoryCedisComponent.DATE_COLUMNS.has(col)) v = InventoryCedisComponent.toIsoDate(v);
        else if (typeof v === 'string') v = v.trim();
        rec[col] = v === '' ? null : v;
        if (rec[col] !== null && rec[col] !== undefined) hasValue = true;
      }
      if (!hasValue) { skipped++; continue; }
      records.push(rec);
    }
    return { records, skipped };
  }

  private buildDiff(records: any[]): Pick<CedisDiffData, 'added' | 'removed' | 'changed' | 'unchanged'> {
    const up = (v: any) => String(v ?? '').trim().toUpperCase();
    const keyOf = this.kind === 'PLANTA'
      ? (r: any) => `${up(r?.['Nombre_Planta'])}|${up(r?.['Sloc'])}|${up(r?.['CFN'])}|${up(r?.['Lote'])}`
      : (r: any) => `${up(r?.['SoldTo'] ?? r?.['NombreCliente'])}|${up(r?.['CFN'])}|${up(r?.['Lote'])}|${up(r?.['Serial'])}`;
    const desc = (r: any) => r?.[this.meta.desc] ?? '';
    const before = new Map<string, any>();
    for (const r of this.rows) before.set(keyOf(r), r);
    const added: CedisDiffRow[] = [];
    const changed: CedisDiffRow[] = [];
    let unchanged = 0;
    const seen = new Set<string>();
    for (const r of records) {
      const k = keyOf(r); seen.add(k);
      const prev = before.get(k);
      const q = this.qtyOf(r);
      if (!prev) added.push({ sapMaterial: r['CFN'] ?? '', description: desc(r), batch: r['Lote'], quantityAfter: q });
      else if (this.qtyOf(prev) !== q) changed.push({ sapMaterial: r['CFN'] ?? '', batch: r['Lote'], quantityBefore: this.qtyOf(prev), quantityAfter: q });
      else unchanged++;
    }
    const removed: CedisDiffRow[] = [];
    for (const [k, prev] of before) {
      if (!seen.has(k)) removed.push({ sapMaterial: prev['CFN'] ?? '', description: desc(prev), batch: prev['Lote'], quantityBefore: this.qtyOf(prev) });
    }
    return { added, removed, changed, unchanged };
  }

  async openUploadSnapshot(): Promise<void> {
    try {
      const items: any = await this.uploadFileDialog.open();
      if (!items || !Array.isArray(items) || items.length === 0) return;

      const { records, skipped } = this.mapExcelRows(items);
      if (records.length === 0) {
        await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: `No se reconoció ningún renglón en el archivo. Revisa que traiga las columnas de ${this.meta.title} (descarga el archivo muestra).`, dismissBtn: true });
        return;
      }
      // Cada reporte trae su columna de cantidad: si no viene, es el archivo del otro módulo.
      if (!records.some(r => r[this.meta.qty] !== null && r[this.meta.qty] !== undefined)) {
        const other = this.kind === 'PLANTA' ? 'Inventario SAP Consigna' : 'Inventario SAP Planta';
        await this.customMessageService.open({ title: 'Archivo de otro reporte', type: 'warning', dismissBtn: true,
          message: `El archivo no trae la columna <b>${this.meta.qty}</b>. Si es el reporte de ${other}, cárgalo en ese módulo.` });
        return;
      }

      const today = new Date();
      const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const diff = this.buildDiff(records);
      const dialogData: CedisDiffData = {
        ...diff,
        comparedWith: this.currentSnapshot ? this.loadLabel(this.selectedLoad ?? this.currentSnapshot) : '',
        fileName: this.uploadFileDialog.lastFileName || `${this.meta.title} ${todayIso}`,
        snapshotDate: todayIso,
        rowCount: records.length,
        skippedRows: skipped,
      };
      const result: { snapshotDate: string; fileName: string } | false = await firstValueFrom(
        this.dialog.open(CedisDiffDialogComponent, { data: dialogData, width: '760px' }).afterClosed());
      if (!result) return;

      this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando corte...', loading: true });
      const response: any = await this.stockSapService.loadSnapshot({
        kind: this.kind,
        snapshotDate: result.snapshotDate,
        fileName: result.fileName,
        products: records,
      });
      this.customMessageService.close();
      if (response.state === 'success') {
        await this.customMessageService.open({ title: 'Listo', type: 'success', message: `Corte guardado: ${records.length} renglones.`, duration: 2500 });
        await this.getLoads();
        this.selectedLoadId = response?.data?.[0]?.load?.id ?? this.loadsList[0]?.id ?? null;
        await this.getSnapshot();
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true, dismissBtnText: 'Cerrar' });
    }
  }

  // ─── Descargas ───────────────────────────────────────────────────────────

  async downloadSnapshot(): Promise<void> {
    const cols = this.presentColumns;
    const tableStruct: TableStructureModel[] = cols.map((c, i) => ({ columnKey: 'c' + i, columnName: c, type: 'string' }));
    const data = this.rows.map(r => { const o: any = {}; cols.forEach((c, i) => o['c' + i] = r?.[c] ?? ''); return o; });
    await this.simpleExcel.simpleXLSX({
      fileName: `${this.meta.file}-${this.currentSnapshot?.snapshotDate || 'sin-fecha'}`,
      sheets: [{ sheetName: 'Corte', columns: tableStruct, data, header: [], lineBreak: 0 }],
    });
  }

  /** Plantilla con las columnas del reporte de este módulo. */
  async downloadSampleFile(): Promise<void> {
    const tableStruct: TableStructureModel[] = this.columns.map((c, i) => ({ columnKey: 'c' + i, columnName: c, type: 'string' }));
    await this.simpleExcel.simpleXLSX({
      fileName: `Muestra-${this.meta.file}`,
      sheets: [{ sheetName: 'Corte', columns: tableStruct, header: [], lineBreak: 0, data: [] }],
    });
  }

  public formatDate(dateString: string): string {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric' });
  }
}
